import React, { useEffect, useMemo, useState } from "react";
import apiRequestCall from "../apiHelper/globalHelper";
import Select from "react-select";

interface FilterOption {
  id: number;
  name: string;
}

type SingleSelected = number | null;
type MultiSelected = number[];

interface BaseProps {
  label: string;
  endpoint: string;
  refreshKey?: any;
  multiple?: boolean;
}

interface Props extends BaseProps {
  selected: SingleSelected | MultiSelected;
  onChange: (updated: SingleSelected | MultiSelected) => void;
  payload?: any; // ✅ added for dependent filters
}

type Option = {
  value: string;
  label: string;
};

const FilterDropdown: React.FC<Props> = ({
  label,
  endpoint,
  selected,
  onChange,
  refreshKey,
  multiple = false,
  payload = {}, // ✅ default empty
}) => {
  const [options, setOptions] = useState<FilterOption[]>([]);
  const safeId = label.replace(/\s+/g, "-").toLowerCase();
  const [isFocused, setIsFocused] = useState(false);

  /* ---------------- FETCH OPTIONS ---------------- */
  useEffect(() => {
    let mounted = true;

    const isDependentEndpoint = [
      "/getUspBrandDD",
      "/getUspPrincipalDD",
      "/getUspDD",
    ].some((url) => endpoint.startsWith(url));

    apiRequestCall(isDependentEndpoint ? "get" : "post", endpoint, {
      ...(payload && Object.keys(payload).length ? { data: payload } : {}),
    })
      .then((res) => {
        const raw = res?.data?.data;
        let parsed: FilterOption[] = [];

        if (raw && typeof raw === "object" && !Array.isArray(raw)) {
          parsed = Object.entries(raw).map(([id, name]) => ({
            id: Number(id),
            name: String(name),
          }));
        } else if (Array.isArray(raw)) {
          parsed = raw.map((item: any) => ({
            id: item.id,
            name:
              item.name ||
              item.part_no ||
              item.description ||
              item.type ||
              String(item.id),
          }));
        }

        if (mounted) setOptions(parsed);
      })
      .catch((err) => {
        console.error(`${label} filter fetch failed`, err);
        setOptions([]);
      });

    return () => {
      mounted = false;
    };
  }, [endpoint, label, refreshKey]); // ✅ payload added

  /* ---------------- OPTIONS ---------------- */
  /* ---------------- OPTIONS ---------------- */
  const selectOptions: Option[] = useMemo(
    () =>
      [...options]
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            sensitivity: "base",
          })
        )
        .map((opt) => ({
          value: String(opt.id),
          label: opt.name,
        })),
    [options]
  );

  /* ---------------- VALUE ---------------- */
  const value = useMemo(() => {
    if (multiple) {
      const selectedIds = (selected as MultiSelected) || [];
      return selectOptions.filter((o) => selectedIds.includes(Number(o.value)));
    }

    return (
      selectOptions.find(
        (o) => o.value === (selected != null ? String(selected) : "")
      ) ?? null
    );
  }, [selected, selectOptions, multiple]);

  /* ---------------- CHANGE HANDLER ---------------- */
  const handleChange = (val: any) => {
    if (!val) {
      onChange(multiple ? [] : null);
      return;
    }

    if (Array.isArray(val)) {
      onChange(val.map((v: any) => Number(v.value)));
    } else {
      onChange(Number(val.value));
    }
  };

  return (
    <div className="filter-dropdown">
      <Select<Option, boolean>
        inputId={safeId}
        isMulti={multiple}
        isClearable
        options={selectOptions}
        value={value}
        placeholder={isFocused ? "Search ..." : `Filter ${label}`}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        styles={{
          container: (base) => ({ ...base, minWidth: 200 }),
          control: (base, state) => ({
            ...base,
            borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
            boxShadow: state.isFocused
              ? "0 0 0 0.1rem rgba(13,110,253,.25)"
              : "none",
            "&:hover": { borderColor: "#86b7fe" },
          }),
        }}
      />
    </div>
  );
};

export default FilterDropdown;
